/**
 * Acquisition attribution carried with every public form submission
 * (spec section 16).
 *
 * This data is strategically important — it is how the September outreach
 * campaign is measured, and it must survive the joinherdbook.com redirect
 * (CLAUDE.md). It is also, always, secondary to the signup itself.
 *
 * So nothing in this schema can fail: every field is trimmed, empty values
 * become absent, over-long values are truncated to their cap rather than
 * rejected, and a session id that does not look like a session id is dropped.
 * A malformed UTM parameter must never be the reason a real horse owner's
 * signup returns a validation error. Unknown keys are stripped by Zod's
 * default object behaviour, so a hand-crafted payload cannot smuggle extra
 * columns through.
 */

import { z } from 'zod';

/** Caps chosen to fit real URLs without letting a payload grow unbounded. */
export const ATTRIBUTION_LIMITS = {
  sessionId: 64,
  landingPath: 512,
  referrer: 1024,
  utm: 200,
  ref: 200,
  clickId: 255,
  variant: 8,
} as const;

/** Trim, drop empties, truncate at the cap. Never errors. */
function cappedText(max: number) {
  return z
    .unknown()
    .transform((value) => {
      if (typeof value !== 'string') return undefined;
      const trimmed = value.trim();
      return trimmed === '' ? undefined : trimmed.slice(0, max);
    })
    .optional();
}

/**
 * A client-generated opaque id (`crypto.randomUUID()` in practice). Validated
 * loosely rather than as a strict UUID so the client is free to change its
 * generator, and dropped — not rejected — when it does not match.
 */
const sessionIdSchema = z
  .unknown()
  .transform((value) => {
    if (typeof value !== 'string') return undefined;
    const trimmed = value.trim();
    if (!/^[A-Za-z0-9_-]{8,64}$/.test(trimmed)) return undefined;
    return trimmed;
  })
  .optional();

export const attributionSchema = z.object({
  sessionId: sessionIdSchema,
  landingPath: cappedText(ATTRIBUTION_LIMITS.landingPath),
  referrer: cappedText(ATTRIBUTION_LIMITS.referrer),
  utmSource: cappedText(ATTRIBUTION_LIMITS.utm),
  utmMedium: cappedText(ATTRIBUTION_LIMITS.utm),
  utmCampaign: cappedText(ATTRIBUTION_LIMITS.utm),
  utmContent: cappedText(ATTRIBUTION_LIMITS.utm),
  utmTerm: cappedText(ATTRIBUTION_LIMITS.utm),
  /** Short campaign code used in outreach links, e.g. `equiery_aug26`. */
  ref: cappedText(ATTRIBUTION_LIMITS.ref),
  /** Ad platform click identifier, when one is present on the landing URL. */
  clickId: cappedText(ATTRIBUTION_LIMITS.clickId),
  /**
   * Landing page A/B assignment, 'a' or 'b', resolved in the browser
   * (`client/lib/attribution.ts`). Capped like everything else here rather
   * than validated against the two known values: an unrecognised variant is a
   * reporting curiosity, never a reason to fail a signup.
   */
  variant: cappedText(ATTRIBUTION_LIMITS.variant),
});

/**
 * Attribution as it appears on a signup payload: optional as a whole, and
 * unparseable input (a string, an array) is treated as "no attribution" rather
 * than as a validation failure, for the same reason as above.
 */
export const optionalAttributionSchema = z.preprocess(
  (value) => (value !== null && typeof value === 'object' && !Array.isArray(value) ? value : {}),
  attributionSchema,
);

export type Attribution = z.infer<typeof attributionSchema>;

/** True when a touch carries no information worth recording. */
export function isEmptyAttribution(attribution: Attribution): boolean {
  return Object.values(attribution).every((value) => value === undefined);
}
